import filesize from 'filesize';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Popover } from '@myua/kit';
import { filterConditionApplied } from '../../../../../../general/modules/filter/actions';
import { ValueTuner } from '../../../../../../general/modules/filter/components/Tuner/ValueTuner';
import {
  Boundary,
  Kind,
  Type,
  KINDS,
  Magnitude,
} from '../../../../../../general/modules/filter/dataModel';
import { FileSizeObject, FMColumn, NumericalFMColumn } from '../../../model';
import { getActiveFilters } from '../../../selectors/selectors';
import { Label } from './Label';

const numericalMagnitudes: Record<Magnitude, number> = {
  B: 2 ** 0,
  KB: 2 ** 10,
  MB: 2 ** 20,
  GB: 2 ** 30,
};
export const FVCBytes: React.FC<{
  column: FMColumn<Type.BYTES>;
}> = ({ column }) => {
  return <FilterValueContainer column={column} />;
};
// MYUA-17039: текстовый фильтр по подстроке (колонка Name). Обёртка — как
// у `FVCBytes`: `plugins` колонки типизированы её `Type`.
export const FVCString: React.FC<{
  column: FMColumn<Type.STRING>;
}> = ({ column }) => {
  return <FilterValueContainer column={column} />;
};
// MYUA-17039: кроме числовых колонок — текстовая (`Type.STRING`, фильтр Name).
export const FilterValueContainer: React.FC<{
  column: NumericalFMColumn | FMColumn<Type.STRING>;
}> = ({ column }) => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const currentActiveFilter = useSelector(getActiveFilters)[column.key];
  const currentActiveFilter_ge = currentActiveFilter?.ge;
  const currentActiveFilter_le = currentActiveFilter?.le;
  const currentActiveFilter_text = currentActiveFilter?.text;

  const [geTemp, setGeTemp] = useState<string | undefined>(undefined);
  const [leTemp, setLeTemp] = useState<string | undefined>(undefined);

  const onCancel = useMemo(
    () => () => {
      const magnitudeSource = currentActiveFilter_ge || currentActiveFilter_le;
      const exponentToMagnitude: Record<number, Magnitude> = {
        '0': 'B',
        '1': 'KB',
        '2': 'MB',
        '3': 'GB',
      };
      const _magnitude: Magnitude =
        column.filter === Type.BYTES
          ? magnitudeSource !== undefined
            ? exponentToMagnitude[
                (filesize(magnitudeSource, { output: 'object' }) as unknown as FileSizeObject)
                  .exponent
              ]
            : 'MB'
          : 'MB';
      setMagnitude(_magnitude);

      const filesizeGe =
        currentActiveFilter_ge !== undefined
          ? (
              filesize(currentActiveFilter_ge, {
                output: 'object',
              }) as unknown as FileSizeObject
            ).value.toString(10)
          : undefined;
      const filesizeLe =
        currentActiveFilter_le !== undefined
          ? (
              filesize(currentActiveFilter_le, {
                output: 'object',
              }) as unknown as FileSizeObject
            ).value.toString(10)
          : undefined;
      setGeTemp(filesizeGe);
      setLeTemp(filesizeLe);
      // MYUA-17039: закрытие без Apply возвращает и текст к применённому.
      setText(currentActiveFilter_text);
      setOpen(false);
    },
    [currentActiveFilter_ge, currentActiveFilter_le, currentActiveFilter_text, column.filter],
  );

  const sanitizedLe = useMemo(
    () => (leTemp !== undefined ? parseFloat(leTemp) : undefined),
    [leTemp],
  );
  const sanitizedGe = useMemo(
    () => (geTemp !== undefined ? parseFloat(geTemp) : undefined),
    [geTemp],
  );

  // MYUA-17039: исходно — применённый текст, иначе при перемонтировании с
  // активным фильтром инпут пуст, а Apply без ввода молча снял бы фильтр.
  const [text, setText] = useState<string | undefined>(currentActiveFilter_text);
  const [magnitude, setMagnitude] = useState<Magnitude>('MB');

  // MYUA-17039: заголовок колонки не перемонтируется при переходе в другую
  // папку, а сага сбрасывает фильтры через `allFiltersRemoved` — без этого
  // в инпуте осталось бы старое значение и Apply применил бы его снова.
  // Открытый поповер не трогаем, чтобы не затереть ввод: при закрытии
  // `onCancel` и так вернёт инпуты к применённому значению.
  useEffect(() => {
    if (
      !open &&
      currentActiveFilter_ge === undefined &&
      currentActiveFilter_le === undefined &&
      currentActiveFilter_text === undefined
    ) {
      setGeTemp(undefined);
      setLeTemp(undefined);
      setText(undefined);
      setMagnitude('MB');
    }
  }, [open, currentActiveFilter_ge, currentActiveFilter_le, currentActiveFilter_text]);

  const disabled = Number(sanitizedGe) > Number(sanitizedLe);
  const { filter } = column;
  if (filter === undefined) return null;
  const kind = KINDS[filter];
  if (kind !== Kind.LITERAL && kind !== Kind.NUMERICAL) return null;

  const onApply = () => {
    if (filter === Type.BYTES) {
      const mul = numericalMagnitudes[magnitude];
      dispatch(
        filterConditionApplied({
          fieldID: column.key,
          condition: {
            ge: sanitizedGe !== undefined ? sanitizedGe * mul : undefined,
            le: sanitizedLe !== undefined ? sanitizedLe * mul : undefined,
          },
        }),
      );
    } else {
      // MYUA-17039: пробелы по краям не участвуют в поиске, а строка из одних
      // пробелов не делает фильтр активным.
      const trimmedText = text?.trim() || undefined;
      dispatch(
        filterConditionApplied({
          fieldID: column.key,
          condition: { ge: sanitizedGe, le: sanitizedLe, text: trimmedText },
        }),
      );
      setText(trimmedText);
    }
    setGeTemp(sanitizedGe?.toString(10));
    setLeTemp(sanitizedLe?.toString(10));
    setOpen(false);
  };

  return (
    <div
      className="ch-file-manager__filter"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Popover
        trigger="click"
        placement="bottomRight"
        // Semantic звал onOpen и onClose раздельно; у antd одна ручка,
        // закрытие по-прежнему означает отмену правки фильтра.
        onOpenChange={(isOpen) => (isOpen ? setOpen(true) : onCancel())}
        open={open}
        content={
          <ValueTuner
            multiplier={magnitude}
            setMultiplier={setMagnitude}
            // MYUA-17039: `text` нужен текстовому инпуту — он контролируемый,
            // без значения в условии набранное в нём не отображалось бы.
            condition={{ ge: geTemp, le: leTemp, text }}
            disabled={disabled}
            itemOnChange={(boundary) => (e: any) => {
              const v: string = e.target.value;
              const setter =
                boundary === Boundary.TEXT
                  ? setText
                  : boundary === Boundary.LE
                    ? setLeTemp
                    : setGeTemp;
              setter(v === '' ? undefined : v);
            }}
            inputOnKeyPress={(e: any) => {
              if (e.key === 'Enter' && !disabled) {
                onApply();
              }
            }}
            applyOnClick={onApply}
            closeOnClick={onCancel}
            clearOnClick={() => {
              setGeTemp(undefined);
              setLeTemp(undefined);
              setText(undefined);
            }}
            type={filter}
          />
        }
      >
        <Label columnKey={column.key} />
      </Popover>
    </div>
  );
};
